import * as React from 'react'
import { UserPersonalSkillsTable } from './table'
import { TableProvider } from './table/context/table'
import { deserialize } from '../../api/utils/deserialize'
import { UserPersonalSkillsTableRow } from './table/data'
import { observer } from 'mobx-react-lite'
import {
  ProfileSkillSidepanel,
  SidepanelMode,
  SkillFormData,
} from 'components/profile-skill-sidepanel'
import { successToast } from '../../utils/success-toast'
import { errorToast } from '../../utils/error-toast'
import { useApiClient } from '../../utils/use-api-client'
import { GlobalConfirmationDialog } from 'src/design-system'

type UserPersonalSkillsPageProps = {
  userId: number
}

export const UserPersonalSkillsPage = observer(
  ({ userId }: UserPersonalSkillsPageProps) => {
    const [allLoading, setAllLoading] = React.useState(true)
    const [data, setData] = React.useState<UserPersonalSkillsTableRow[]>([])
    const [sidepanelMode, setSidepanelMode] =
      React.useState<SidepanelMode>('closed')
    const [selectedSkill, setSelectedSkill] =
      React.useState<UserPersonalSkillsTableRow | null>(null)
    const client = useApiClient()

    const fetchUserPersonalSkills = React.useCallback(async () => {
      try {
        const json = await client.get(
          `/api/v1/internal/users/${userId}/profile_skills`
        )
        const deserialized = deserialize(json)
        if (!deserialized?.data) {
          setData([])
          return
        }
        const skills = deserialized.data.userPersonalSkills as
          | Record<string, UserPersonalSkillsTableRow>
          | undefined
        setData(skills ? Object.values(skills).map(normalizeRow) : [])
      } catch {
        errorToast()
        setData([])
      } finally {
        setAllLoading(false)
      }
    }, [client, userId])

    const handleAddSkill = async (formData: SkillFormData) => {
      try {
        await client.post(`/api/v1/internal/users/${userId}/profile_skills`, {
          profile_skills_user: {
            skill_name: formData.name,
            description: formData.description,
            skill_type: formData.skill_type,
            proficiency_level: formData.proficiency_level,
            starred: formData.starred,
          },
        })
        await fetchUserPersonalSkills()
        successToast('Skill added!')
      } catch {
        errorToast()
      }
    }

    const handleUpdateSkill = async (
      profileSkillId: string,
      formData: SkillFormData
    ) => {
      try {
        await client.put(
          `/api/v1/internal/users/${userId}/profile_skills/${profileSkillId}`,
          {
            profile_skills_user: {
              description: formData.description,
              skill_type: formData.skill_type,
              proficiency_level: formData.proficiency_level,
              starred: formData.starred,
            },
          }
        )
        await fetchUserPersonalSkills()
        successToast('Skill updated!')
      } catch {
        errorToast()
      }
    }

    const handleDeleteSkill = async (skillId: string) => {
      const skill = data.find((s) => s.id === skillId)
      if (!skill) return

      try {
        await client.delete(
          `/api/v1/internal/users/${userId}/profile_skills/${skill.profileSkillId}`
        )
        setData((prevData) => prevData.filter((s) => s.id !== skillId))
        setSidepanelMode('closed')
        setSelectedSkill(null)
        successToast('Skill deleted!')
      } catch {
        errorToast()
      }
    }

    const handleCloseSidepanel = () => {
      setSidepanelMode('closed')
      setSelectedSkill(null)
    }

    const handleOpenAddSkill = () => {
      setSidepanelMode('add')
      setSelectedSkill(null)
    }

    const handleViewSkill = (skillId: string) => {
      const skill = data.find((s) => s.id === skillId)
      if (skill) {
        setSelectedSkill(skill)
        setSidepanelMode('view')
      }
    }

    const handleToggleStar = async (skillId: string, starred: boolean) => {
      const skill = data.find((s) => s.id === skillId)
      if (!skill) return

      try {
        await client.put(
          `/api/v1/internal/users/${userId}/profile_skills/${skill.profileSkillId}`,
          {
            profile_skills_user: { starred },
          }
        )
        // Update local data - also update source since backend sets it to self_declared
        setData((prevData) =>
          prevData.map((s) =>
            s.id === skillId ? { ...s, starred, source: 'self_declared' } : s
          )
        )
        // Update selected skill if it's the one being toggled
        if (selectedSkill?.id === skillId) {
          setSelectedSkill({
            ...selectedSkill,
            starred,
            source: 'self_declared',
          })
        }
        successToast(starred ? 'Skill starred!' : 'Star removed!')
      } catch {
        errorToast()
      }
    }

    const handleDeclareSkill = async (skillId: string) => {
      const skill = data.find((s) => s.id === skillId)
      if (!skill) return

      try {
        // Call update API - backend will set source to self_declared
        await client.put(
          `/api/v1/internal/users/${userId}/profile_skills/${skill.profileSkillId}`,
          {
            profile_skills_user: { starred: skill.starred },
          }
        )
        // Update local data
        setData((prevData) =>
          prevData.map((s) =>
            s.id === skillId ? { ...s, source: 'self_declared' } : s
          )
        )
        // Update selected skill
        if (selectedSkill?.id === skillId) {
          setSelectedSkill({ ...selectedSkill, source: 'self_declared' })
        }
        successToast('Skill declared!')
      } catch {
        errorToast()
      }
    }

    React.useEffect(() => {
      fetchUserPersonalSkills()
    }, [fetchUserPersonalSkills])

    return (
      <TableProvider
        loading={allLoading}
        data={data}
        onDeleteSkill={handleDeleteSkill}
        onViewSkill={handleViewSkill}
      >
        <UserPersonalSkillsTable onAddSkillsClick={handleOpenAddSkill} />
        <ProfileSkillSidepanel
          mode={sidepanelMode}
          skill={selectedSkill}
          userId={userId.toString()}
          onClose={handleCloseSidepanel}
          onModeChange={setSidepanelMode}
          onAddSkill={handleAddSkill}
          onUpdateSkill={handleUpdateSkill}
          onDeleteSkill={handleDeleteSkill}
          onToggleStar={handleToggleStar}
          onDeclareSkill={handleDeclareSkill}
        />
        <GlobalConfirmationDialog id="delete-profile-skill" />
      </TableProvider>
    )
  }
)

function normalizeRow(
  row: UserPersonalSkillsTableRow & Record<string, unknown>
): UserPersonalSkillsTableRow {
  return {
    id: row.id?.toString(),
    profileSkillId: row.profileSkillId?.toString(),
    name: row.name,
    description: row.description,
    skillType: row.skillType,
    proficiencyLevel: row.proficiencyLevel,
    source: row.source,
    starred: row.starred ?? false,
  }
}
